// D-1017: 「稼働ブース」= booths.is_active かつ 親の machines.is_active。
//   ひろ 2026-10-08: 撤去した BUZZmini④(ODP01-M10)のブースが集金入力に出続けた。機械の無効化が
//   booths に連動しておらず、集金一覧は booths.is_active だけを見ていた(本番で 14 台 / 23 ブース)。
//   ここは 2 つだけを持つ:
//     - keepBoothsOfActiveMachines: ブース起点の一覧から、親機械が無効なブースを外す(表示対象の絞り込みだけ)。
//     - deactivateBoothsOfMachine:  機械を無効化した操作の中で、その機械の稼働ブースを無効化する。
//   ★再稼働(無効→有効)ではブースを戻さない。撤去前にどのブースが生きていたかは分からないため、
//     ブースは店舗ドロワーのブース個別トグルで戻す(従来の UI のまま)。
import { supabase } from '../lib/supabase'

/**
 * 親機械が稼働中のブースだけを残す。
 * ★親機械の行が見つからないブースは残す(fail-open)。権限や取得失敗で集金対象を黙って消さない。
 * @param {Array<{machine_code:string}>} booths
 * @param {Array<{machine_code:string,is_active?:boolean}>|Record<string,{is_active?:boolean}>} machines 配列か machine_code→行 の map
 */
export function keepBoothsOfActiveMachines(booths, machines) {
  const byCode = Array.isArray(machines)
    ? Object.fromEntries(machines.map(m => [m.machine_code, m]))
    : (machines ?? {})
  return (booths ?? []).filter(b => byCode[b.machine_code]?.is_active !== false)
}

/**
 * D-1024: 機械起点で booths を embed した行から、稼働ブースの booth_code を並べて返す(昇順)。
 * 稼働ブース = booths.is_active かつ 親の machines.is_active。値が無い行は残す(fail-open)。
 * @param {Array<{is_active?:boolean, booths?:Array<{booth_code:string,is_active?:boolean}>}>} machines
 * @returns {string[]}
 */
export function activeBoothCodesOfMachines(machines) {
  return (machines ?? [])
    .filter(m => m?.is_active !== false)
    .flatMap(m => (m.booths ?? []).filter(b => b?.is_active !== false).map(b => b.booth_code))
    .sort()
}

/**
 * 機械の稼働ブースを無効化する(機械の無効化と同じ操作の中で呼ぶ)。
 * @returns {Promise<{boothCodes:string[], error:Error|null}>}
 */
export async function deactivateBoothsOfMachine(machineCode, { updatedBy } = {}) {
  const payload = { is_active: false, updated_at: new Date().toISOString() }
  if (updatedBy) payload.updated_by = updatedBy
  // eslint-disable-next-line supabase-guard/require-order -- 1 機械のブースだけの UPDATE。並び順は使わない
  const { data, error } = await supabase
    .from('booths')
    .update(payload)
    .eq('machine_code', machineCode)
    .eq('is_active', true)
    .select('booth_code')
  if (error) return { boothCodes: [], error }
  return { boothCodes: (data ?? []).map(b => b.booth_code), error: null }
}

// D-1018: 店舗一覧の「ブース n」= 稼働ブースだけ(booths.is_active かつ 親の machines.is_active)。
//   ★親機械の行が取れないブースは数える(fail-open。keepBoothsOfActiveMachines と同じ考え)。
/**
 * @param {Array<{store_code:string,is_active?:boolean,machines?:{is_active?:boolean}|Array<{is_active?:boolean}>|null}>} booths
 * @returns {Record<string, number>} store_code → 稼働ブース数
 */
export function countActiveBoothsByStore(booths) {
  const counts = {}
  for (const b of booths ?? []) {
    if (b?.is_active === false) continue
    const m = Array.isArray(b?.machines) ? b.machines[0] : b?.machines
    if (m?.is_active === false) continue
    counts[b.store_code] = (counts[b.store_code] ?? 0) + 1
  }
  return counts
}

const COUNT_PAGE = 1000
/**
 * 店舗ごとの稼働ブース数を引く(PostgREST の 1000 行で切れないよう range でページを送る)。
 * @param {string[]} storeCodes
 * @returns {Promise<Record<string, number>>}
 */
export async function fetchActiveBoothCountsByStore(storeCodes) {
  if (!storeCodes?.length) return {}
  // ★表示用の数字なので、取れなかったら取れた分で返す(画面を止めない。旧実装もエラーは無視していた)。
  const rows = []
  try {
    for (let from = 0; ; from += COUNT_PAGE) {
      const { data, error } = await supabase
        .from('booths')
        .select('booth_code, store_code, is_active, machines!machine_code(is_active)')
        .in('store_code', storeCodes)
        .eq('is_active', true)
        .order('booth_code', { ascending: true })
        .range(from, from + COUNT_PAGE - 1)
      if (error) break
      rows.push(...(data ?? []))
      if (!data || data.length < COUNT_PAGE) break
    }
  } catch (e) {
    console.error('稼働ブース数の取得エラー:', e?.message ?? e)
  }
  return countActiveBoothsByStore(rows)
}
